import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from 'reactstrap';
import Header from 'components/Headers/Header';
import { GlobalContext } from 'api/GlobalContext';
import { fetchUserDetails } from 'components/Misc/apiUtils';
import './NewReleases.css';

const PAGE_SIZE = 25;
const NEW_FOR_DAYS = 14;
const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'feature', label: 'Features' },
  { key: 'bug', label: 'Fixes' },
];

const norm = (s) => String(s || '').toLowerCase().trim();
// 'WebPT 2.0' / 'webpt', 'StrideThera' / 'Stride', 'Net Health' / 'NetHealth' all match.
const emrKey = (s) => norm(s).replace(/[^a-z0-9]/g, '').replace(/\d+$/, '');
const NO_EMR = ['', 'other', 'none'];

function formatDate(dateString) {
  return new Date(`${dateString}T00:00:00`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function isRecent(dateString) {
  const ageMs = Date.now() - new Date(`${dateString}T00:00:00`).getTime();
  return ageMs >= 0 && ageMs < NEW_FOR_DAYS * 864e5;
}

// Entries with no emrs apply to everyone; EMR-specific ones only to that EMR's users.
// Users with no known EMR see everything.
export function releasesForEmr(list, userEmr) {
  const emr = emrKey(userEmr);
  if (NO_EMR.includes(emr)) return list;
  return list.filter(
    (r) => !r.emrs?.length || r.emrs.some((e) => emrKey(e).startsWith(emr) || emr.startsWith(emrKey(e))),
  );
}

const ReleaseCard = ({ release, open, onToggle }) => {
  const navigate = useNavigate();
  const isFix = release.type === 'bug';
  const hasMedia = release.imageLinks?.length || release.videoUrls?.length;

  const openLink = (e) => {
    e.stopPropagation();
    if (/^https?:\/\//.test(release.updateLink)) window.open(release.updateLink, '_blank', 'noopener,noreferrer');
    else navigate(release.updateLink);
  };

  return (
    <div className={`wn-card ${open ? 'wn-card--open' : ''}`}>
      <button
        type="button"
        className="wn-card__head"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`wn-card-${release.id}`}
      >
        <span className={`wn-icon ${isFix ? 'wn-icon--fix' : 'wn-icon--feature'}`} aria-hidden="true">
          <i className={isFix ? 'fa fa-wrench' : 'ni ni-spaceship'} />
        </span>
        <span className="wn-card__title">
          {release.name}
          <span className="wn-card__meta">
            <span className={`wn-pill ${isFix ? 'wn-pill--fix' : 'wn-pill--feature'}`}>
              {isFix ? 'Fix' : 'Feature'}
            </span>
            {release.emrs?.map((e) => (
              <span key={e} className="wn-pill wn-pill--emr">{e}</span>
            ))}
            {isRecent(release.date) && <span className="wn-pill wn-pill--new">New</span>}
          </span>
        </span>
        <i className={`fa fa-chevron-${open ? 'up' : 'down'} wn-card__chevron`} aria-hidden="true" />
      </button>

      {open && (
        <div className="wn-card__body" id={`wn-card-${release.id}`}>
          {release.description.map((d, i) => (
            <p key={i}>{d}</p>
          ))}
          {hasMedia ? (
            <div className="wn-media">
              {release.imageLinks?.map((src, i) => (
                <img key={src} src={src} alt={`${release.name} ${i + 1}`} loading="lazy" />
              ))}
              {release.videoUrls?.map((src, i) => (
                <iframe key={src} src={src} title={`${release.name} video ${i + 1}`} frameBorder="0" allowFullScreen />
              ))}
            </div>
          ) : null}
          {release.updateLink && (
            <button type="button" className="wn-link" onClick={openLink}>
              Take me there <i className="fa fa-arrow-right" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const NewReleases = () => {
  const { apiClient } = useContext(GlobalContext);
  const [userEmr, setUserEmr] = useState(apiClient?.EMR || '');
  const [releases, setReleases] = useState(null);

  // Separate chunk: the full history is large and only this page needs it.
  useEffect(() => {
    let cancelled = false;
    import('data/releases.json').then((m) => !cancelled && setReleases(m.default));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!apiClient) return;
    fetchUserDetails(apiClient).then((data) => data?.EMR && setUserEmr(data.EMR));
  }, [apiClient]);

  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const visible = useMemo(() => {
    const q = norm(query);
    return releasesForEmr(releases || [], userEmr).filter(
      (r) =>
        (type === 'all' || r.type === type) &&
        (!q || norm(r.name).includes(q) || r.description.some((d) => norm(d).includes(q))),
    );
  }, [releases, userEmr, query, type]);

  // releases.json is newest-first; group consecutive entries by date + version.
  const groups = useMemo(
    () =>
      visible.slice(0, limit).reduce((acc, r) => {
        const last = acc[acc.length - 1];
        if (last && last.date === r.date && last.version === r.changeLogid) last.items.push(r);
        else acc.push({ date: r.date, version: r.changeLogid, items: [r] });
        return acc;
      }, []),
    [visible, limit],
  );

  return (
    <>
      <Header isLite={true} />
      <Container className="mt--7" fluid>
        <div className="wn">
          <div className="wn-hero">
            <div className="wn-hero__intro">
              <span className="wn-hero__icon" aria-hidden="true">
                <i className="ni ni-spaceship" />
              </span>
              <div>
                <h2 className="wn-hero__title">What's New in Comprehend</h2>
                <p className="wn-hero__sub">The latest features and fixes, tailored to your EMR.</p>
              </div>
            </div>
            <div className="wn-toolbar">
              <div className="wn-search">
                <i className="fa fa-search" aria-hidden="true" />
                <input
                  type="search"
                  aria-label="Search updates"
                  placeholder="Search updates"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setLimit(PAGE_SIZE);
                  }}
                />
              </div>
              <div className="wn-segment" role="group" aria-label="Filter updates by type">
                {TYPE_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    aria-pressed={type === f.key}
                    aria-controls="wn-timeline"
                    className={type === f.key ? 'active' : ''}
                    onClick={() => {
                      setType(f.key);
                      setLimit(PAGE_SIZE);
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {groups.length ? (
            <div className="wn-timeline" id="wn-timeline">
              {groups.map((g) => (
                <section key={`${g.date}-${g.items[0].id}`} className="wn-group">
                  <h3 className="wn-group__date">
                    {formatDate(g.date)}
                    {g.version && <span className="wn-version">v{g.version}</span>}
                  </h3>
                  {g.items.map((r) => (
                    <ReleaseCard
                      key={r.id}
                      release={r}
                      open={openId === r.id}
                      onToggle={() => setOpenId(openId === r.id ? null : r.id)}
                    />
                  ))}
                </section>
              ))}
              {visible.length > limit && (
                <button type="button" className="wn-more" onClick={() => setLimit(limit + PAGE_SIZE)}>
                  Show older updates
                </button>
              )}
            </div>
          ) : (
            <div className="wn-empty" id="wn-timeline">
              {releases ? 'No updates match your search.' : 'Loading updates…'}
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default NewReleases;
